import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  TextField,
  InputAdornment
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GavelIcon from '@mui/icons-material/Gavel';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading, setAlertData } from "../../Redux/Screen/Action";
import AdminApi from "Service/Admin.api";
import OdrApi from "Service/Odr.api";
import { StyledTableCell, StyledTableRow } from "./ProfessionalUserList";
import ColorData from "Components/CstmTheme/ColorData";

type CaseItem = {
  pkCase: number;
  caseNumber: string;
  description: string;
  casePrice: number;
  dateCreated: string;
  orderType: {
    pkOrderType: number;
    orderTypeName: string;
    orderPrice: number;
  };
  client: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
};

type ProfessionalItem = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  activeCaseCount: number;
};

const BulkNeutralAssignment: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [selectedCaseIds, setSelectedCaseIds] = useState<number[]>([]);
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>("Mediation");
  const [professionals, setProfessionals] = useState<ProfessionalItem[]>([]);
  const [selectedProfId, setSelectedProfId] = useState<number | "">("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Result Dialog State
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [assignmentResults, setAssignmentResults] = useState<any[]>([]);

  useEffect(() => {
    fetchUnassignedCases();
  }, []);

  useEffect(() => {
    // Whenever orderTypeFilter changes, fetch eligible professionals for that role
    fetchEligibleProfessionals();
    setSelectedCaseIds([]); // Clear selection on filter change
    setSelectedProfId(""); // Clear professional selection
  }, [orderTypeFilter]);

  const fetchUnassignedCases = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Fetching unassigned ODR cases..." }));
    try {
      const res = await AdminApi.getAllUnAssignedProfessionalCases();
      if (res?.success && Array.isArray(res.data)) {
        setCases(res.data);
      } else {
        setCases([]);
      }
    } catch (error) {
      console.error("Error fetching unassigned cases:", error);
      dispatch(
        setAlertData({
          alertMessage: "Failed to fetch unassigned ODR cases.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    } finally {
      dispatch(stopLoading());
    }
  };

  const fetchEligibleProfessionals = async () => {
    // Roles in DB: Arbitrator, Mediator, Negotiator
    let role = "Mediator";
    if (orderTypeFilter === "Arbitration") role = "Arbitrator";
    else if (orderTypeFilter === "Negotiation") role = "Negotiator";

    try {
      const res = await OdrApi.getEligibleProfessionals(role);
      if (res && res.success && Array.isArray(res.data)) {
        setProfessionals(res.data);
      } else {
        setProfessionals([]);
      }
    } catch (error) {
      console.error("Error fetching eligible professionals:", error);
    }
  };

  // Filter cases on clientside based on orderTypeFilter and searchQuery
  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      // 1. Order Type Matching
      const typeName = c.orderType?.orderTypeName || "";
      let matchesType = false;
      if (orderTypeFilter === "Mediation") {
        matchesType = typeName.includes("Mediation");
      } else if (orderTypeFilter === "Arbitration") {
        matchesType = typeName.includes("Arbitration");
      } else if (orderTypeFilter === "Negotiation") {
        matchesType = typeName.includes("Negotiation");
      }

      if (!matchesType) return false;

      // 2. Search Query Matching
      const q = searchQuery.toLowerCase().trim();
      if (!q) return true;

      const caseNo = c.caseNumber?.toLowerCase() || "";
      const clientName = `${c.client?.firstName || ""} ${c.client?.lastName || ""}`.toLowerCase();
      const clientEmail = c.client?.email?.toLowerCase() || "";
      const desc = c.description?.toLowerCase() || "";

      return caseNo.includes(q) || clientName.includes(q) || clientEmail.includes(q) || desc.includes(q);
    });
  }, [cases, orderTypeFilter, searchQuery]);

  const selectedProfessional = useMemo(() => {
    return professionals.find(p => p.id === selectedProfId);
  }, [professionals, selectedProfId]);

  const remainingCapacity = useMemo(() => {
    if (!selectedProfessional) return 0;
    return Math.max(0, 100 - selectedProfessional.activeCaseCount);
  }, [selectedProfessional]);

  const capacityExceeded = selectedCaseIds.length > remainingCapacity;

  const handleSelectCase = (caseId: number) => {
    setSelectedCaseIds((prev) =>
      prev.includes(caseId) ? prev.filter((id) => id !== caseId) : [...prev, caseId]
    );
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = filteredCases.map((c) => Number(c.pkCase));
      setSelectedCaseIds(allIds);
    } else {
      setSelectedCaseIds([]);
    }
  };

  const handleAssign = async () => {
    if (!selectedProfId || selectedCaseIds.length === 0 || capacityExceeded) return;

    dispatch(startLoading({ loading: true, loadtext: `Assigning ${selectedCaseIds.length} cases...` }));
    try {
      const res = await OdrApi.assignOdrNeutralBatch({
        neutralId: Number(selectedProfId),
        caseIds: selectedCaseIds
      });

      if (res?.success) {
        setAssignmentResults(res.results || []);
        setResultDialogOpen(true);

        // Refresh case list by removing successfully assigned cases
        const successfulCaseIds = (res.results || [])
          .filter((r: any) => r.success)
          .map((r: any) => Number(r.caseId));

        setCases((prevCases) =>
          prevCases.filter((c) => !successfulCaseIds.includes(Number(c.pkCase)))
        );
        setSelectedCaseIds([]);
        setSelectedProfId("");
        
        // Re-fetch professionals to update their active case loads
        fetchEligibleProfessionals();
      } else {
        dispatch(
          setAlertData({
            alertMessage: res?.message || "Failed to process bulk assignment.",
            alertVariant: "error",
            openAlert: true,
          })
        );
      }
    } catch (error: any) {
      console.error("Bulk assignment error:", error);
      dispatch(
        setAlertData({
          alertMessage: error?.message || "An error occurred during assignment.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    } finally {
      dispatch(stopLoading());
    }
  };

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      {/* Header section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ fontWeight: 'bold', textTransform: 'none' }}
        >
          Back
        </Button>
      </Box>

      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
        <GavelIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Bulk ODR Neutral Assignment
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Propose ODR Neutral professionals to multiple unassigned cases of the same type at once.
      </Typography>

      <Grid container spacing={3} mb={3}>
        {/* Selection & Action Dashboard Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                <PeopleIcon color="action" /> Assignment Details
              </Typography>

              {/* 1. Order Type Selector */}
              <Box mt={2} mb={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="order-type-select-label">Dispute / Order Type</InputLabel>
                  <Select
                    labelId="order-type-select-label"
                    value={orderTypeFilter}
                    label="Dispute / Order Type"
                    onChange={(e) => setOrderTypeFilter(e.target.value)}
                  >
                    <MenuItem value="Mediation">Online Mediation (Mediator)</MenuItem>
                    <MenuItem value="Arbitration">Online Arbitration (Arbitrator)</MenuItem>
                    <MenuItem value="Negotiation">Online Negotiation (Negotiator)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* 2. Professional Picker */}
              <Box mb={2.5}>
                <FormControl fullWidth size="small" disabled={professionals.length === 0}>
                  <InputLabel id="professional-select-label">Select Neutral Professional</InputLabel>
                  <Select
                    labelId="professional-select-label"
                    value={selectedProfId}
                    label="Select Neutral Professional"
                    onChange={(e) => setSelectedProfId(e.target.value as number)}
                  >
                    {professionals.map((p) => {
                      const load = p.activeCaseCount;
                      const remaining = Math.max(0, 100 - load);
                      return (
                        <MenuItem key={p.id} value={p.id}>
                          {p.firstName} {p.lastName} (Active: {load}/100, Remaining: {remaining})
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {professionals.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    No active ODR professionals registered for this role.
                  </Typography>
                )}
              </Box>

              {/* 3. Summary stats */}
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 2, mb: 3 }}>
                <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <span>Selected Cases:</span>
                  <strong>{selectedCaseIds.length}</strong>
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <span>Professional Capacity:</span>
                  <strong>{selectedProfessional ? `${selectedProfessional.activeCaseCount}/100 cases` : "N/A"}</strong>
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Slots Left:</span>
                  <strong style={{ color: remainingCapacity > 0 ? ColorData.primary.main : 'inherit' }}>
                    {selectedProfessional ? remainingCapacity : "N/A"}
                  </strong>
                </Typography>
              </Box>

              {/* Warnings */}
              {capacityExceeded && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Selected cases ({selectedCaseIds.length}) exceed professional's remaining capacity ({remainingCapacity}).
                </Alert>
              )}

              {/* Action Button */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleAssign}
                disabled={selectedCaseIds.length === 0 || !selectedProfId || capacityExceeded}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  py: 1.2,
                  bgcolor: ColorData.primary.main,
                  color: ColorData.text.secondary,
                  '&:hover': {
                    bgcolor: ColorData.hover.main,
                    color: ColorData.text.primary,
                  }
                }}
              >
                Assign Neutral to {selectedCaseIds.length} Cases
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Case List Table Grid */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight="bold">
                Unassigned ODR Cases ({filteredCases.length} available)
              </Typography>
              <TextField
                placeholder="Search cases..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ maxWidth: 300, width: '100%' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            <TableContainer sx={{ maxHeight: 450 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedCaseIds.length > 0 && selectedCaseIds.length < filteredCases.length}
                        checked={filteredCases.length > 0 && selectedCaseIds.length === filteredCases.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Case ID / Number</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Dispute Type</TableCell>
                    <TableCell align="right">Claim Amt (₹)</TableCell>
                    <TableCell>Created Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCases.map((c) => {
                    const isSelected = selectedCaseIds.includes(Number(c.pkCase));
                    return (
                      <TableRow
                        key={c.pkCase}
                        hover
                        selected={isSelected}
                        onClick={() => handleSelectCase(Number(c.pkCase))}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectCase(Number(c.pkCase))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{c.caseNumber}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 220 }}>
                            {c.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{c.client?.firstName} {c.client?.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary">{c.client?.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={c.orderType?.orderTypeName} size="small" variant="outlined" color="primary" />
                        </TableCell>
                        <TableCell align="right">
                          {c.casePrice ? Number(c.casePrice).toLocaleString('en-IN') : "0"}
                        </TableCell>
                        <TableCell>
                          {c.dateCreated ? new Date(c.dateCreated).toLocaleDateString() : ""}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredCases.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No unassigned ODR cases found matching this criteria.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Results Summary Modal */}
      <Dialog
        open={resultDialogOpen}
        onClose={() => setResultDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" /> Assignment Summary
        </DialogTitle>
        <DialogContent>
          <Box py={1}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Bulk assignment task completed. See below for details on each case.
            </Alert>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Case ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reason / Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignmentResults.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>Case #{r.caseId}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.success ? "Success" : "Failed"}
                          color={r.success ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={r.success ? "text.primary" : "error.main"}>
                          {r.message}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setResultDialogOpen(false)}
            variant="contained"
            sx={{
              textTransform: 'none',
              bgcolor: ColorData.primary.main,
              color: ColorData.text.secondary,
              '&:hover': { bgcolor: ColorData.hover.main }
            }}
          >
            Close Summary
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkNeutralAssignment;
